// The calendar's filter bar, in the page HEADER beside the title (via
// `PageToolbar`) like the live map's — the board gets its full height.
//
// Every control here REALLY filters the rows, the lists and the counts
// together; the page applies them once (CalendarBoardPage).

import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Separator } from "@bednbody/ui/components/separator";
import { ToggleGroup, ToggleGroupItem } from "@bednbody/ui/components/toggle-group";
import { cn } from "@bednbody/ui/lib/utils";
import { CaretLeftIcon, CaretRightIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { PageToolbar } from "@/components/PageToolbar";
import type { CalendarFilters, CalendarHub, CalendarWho } from "./calendar-board";

export type CalendarView = "day" | "week";

const WHO: readonly { value: CalendarWho; label: string }[] = [
  { value: "all", label: "Everyone" },
  { value: "therapists", label: "Therapists only" },
  { value: "riders", label: "Riders only" },
];

/** A select that sits INSIDE the filter pill: no fill or border of its own. */
const BARE_SELECT =
  "[&_select]:h-7 [&_select]:border-0 [&_select]:bg-transparent [&_select]:font-medium [&_select]:hover:bg-muted/60";

interface CalendarToolbarProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  dateTitle: string;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  hubs: readonly CalendarHub[];
  filters: CalendarFilters;
  onFiltersChange: (filters: CalendarFilters) => void;
}

export function CalendarToolbar({
  view,
  onViewChange,
  dateTitle,
  onPrevious,
  onNext,
  onToday,
  hubs,
  filters,
  onFiltersChange,
}: CalendarToolbarProps) {
  const step = view === "day" ? "day" : "week";
  return (
    <PageToolbar>
      <div
        aria-label="Calendar filters"
        className="flex min-w-0 flex-1 items-center gap-3"
        role="group"
      >
        <div className="flex shrink-0 items-center gap-1">
          <Button aria-label={`Previous ${step}`} onClick={onPrevious} size="icon" variant="ghost">
            <CaretLeftIcon />
          </Button>
          <Button
            className="border-border bg-card font-semibold shadow-xs"
            onClick={onToday}
            title="Back to today"
            variant="outline"
          >
            {dateTitle}
          </Button>
          <Button aria-label={`Next ${step}`} onClick={onNext} size="icon" variant="ghost">
            <CaretRightIcon />
          </Button>
        </div>

        <ToggleGroup
          aria-label="View"
          className="shrink-0 rounded-full border border-border bg-card p-1 shadow-xs"
          onValueChange={(values) => {
            const next = values[0] as CalendarView | undefined;
            if (next) onViewChange(next);
          }}
          size="sm"
          spacing={1}
          value={[view]}
        >
          {(["day", "week"] as const).map((value) => (
            <ToggleGroupItem
              className="h-7 rounded-full px-3 font-medium text-muted-foreground aria-pressed:bg-primary/10 aria-pressed:text-primary-dark"
              key={value}
              value={value}
            >
              {value === "day" ? "Day" : "Week"}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="relative min-w-32 max-w-56 flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search name or booking reference"
            className="w-full border-border bg-card pl-10 shadow-xs"
            maxLength={60}
            onChange={(event) => onFiltersChange({ ...filters, search: event.target.value })}
            placeholder="Name or booking"
            type="search"
            value={filters.search}
          />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1 rounded-full border border-border bg-card p-1 shadow-xs">
          <label className="sr-only" htmlFor="calendar-hub">
            Hub
          </label>
          <NativeSelect
            className={cn("w-30 shrink-0", BARE_SELECT)}
            id="calendar-hub"
            onChange={(event) => onFiltersChange({ ...filters, hubId: event.target.value || null })}
            size="sm"
            value={filters.hubId ?? ""}
          >
            <NativeSelectOption value="">All hubs</NativeSelectOption>
            {hubs.map((hub) => (
              <NativeSelectOption key={hub.id} value={hub.id}>
                {hub.name} hub
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Separator className="h-5" orientation="vertical" />
          <label className="sr-only" htmlFor="calendar-who">
            Who
          </label>
          <NativeSelect
            className={cn("w-34 shrink-0", BARE_SELECT)}
            id="calendar-who"
            onChange={(event) =>
              onFiltersChange({ ...filters, who: event.target.value as CalendarWho })
            }
            size="sm"
            value={filters.who}
          >
            {WHO.map((option) => (
              <NativeSelectOption key={option.value} value={option.value}>
                {option.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      </div>
    </PageToolbar>
  );
}
